<script lang="ts" setup>
import { computed } from 'vue';

import {
  getMaxLengthValidationRule,
  requiredValidationRule,
} from '@/helpers/validationRules';

import fieldStyles from '@/styles/field.module.scss';

export type FieldSize = 'xs' | 'sm' | 'md' | 'lg';

interface Props {
  modelValue?: string | null;
  size?: FieldSize;
  loading?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  size: 'md',
  loading: false,
  disabled: false,
  readonly: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const sizeClass = computed(() => 'size-' + props.size);

const fieldValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const maxLength = 16;
const fieldValidationRules = [
  requiredValidationRule,
  getMaxLengthValidationRule(maxLength),
];

// TODO: common function
const onlyNumberAndSpace = (event: KeyboardEvent): void => {
  if (props.readonly) return;
  const keyCode = event.keyCode ? event.keyCode : event.which;
  if (
    (keyCode < 48 || keyCode > 57) &&
    keyCode !== 32 &&
    keyCode !== 40 &&
    keyCode !== 41
  ) {
    event.preventDefault();
  }
};
</script>

<template>
  <v-text-field
    v-model.trim="fieldValue"
    label="Phone"
    min="0"
    oninput="if (this.value < 0) this.value = 0;"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :maxlength="maxLength"
    :loading="props.loading"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rules="fieldValidationRules"
    :class="['g-v-text-field', $style.phoneInput, fieldStyles[sizeClass]]"
    data-testid="phoneInput"
    @keypress="onlyNumberAndSpace"
  ></v-text-field>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.phoneInput {
  :global {
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
